import { CommonModule } from '@angular/common'
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  ViewEncapsulation,
} from '@angular/core'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import { AnimationOptions } from 'ngx-lottie'
import { Observable, Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { getTheme } from 'src/app/ngrx/store/selectors'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { FigureService } from 'src/app/service/data/figure.service'
import { NoteService } from 'src/app/service/data/note.service'
import { PairService } from 'src/app/service/data/pair.service'
import { QuizService } from 'src/app/service/data/quiz.service'
import { WordService } from 'src/app/service/data/word.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { NetworkService } from 'src/app/service/network/network.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { DdcTabsChipsComponent } from 'src/app/shared/ddc-tabs-chips'
import { DdcContentCardComponent } from 'src/app/shared/ddc/ddc-content-card/ddc-content-card.component'
import { DdcLoadingSkeletonComponent } from 'src/app/shared/ddc/ddc-loading-skeleton/ddc-loading-skeleton.component'
import { DdcUserProfileCardComponent } from 'src/app/shared/ddc/ddc-user-profile-card/ddc-user-profile-card.component'
import {
  ContentTypes,
  DaydicTypesIconNames,
  DefaultPagination,
  LibraryChips,
  Media,
  Themes,
} from 'src/app/shared/enums/enums'

import { Store } from '@ngrx/store'
import { AppState } from 'src/app/ngrx/states'
import { ContentTypeKey, LibraryStateService } from 'src/app/service/state/library-state.service'
import { DdcDeleteDialogComponent } from 'src/app/shared/ddc/ddc-delete-dialog/ddc-delete-dialog.component'
import { DdcDesktopDialogComponent } from 'src/app/shared/ddc/ddc-desktop-dialog/ddc-desktop-dialog.component'
import { DdcMobileSheetComponent } from 'src/app/shared/ddc/ddc-mobile-sheet/ddc-mobile-sheet.component'
import { Dictionary } from 'src/app/shared/models/main/dictionary'
import { Figure } from 'src/app/shared/models/main/figure'
import { Note } from 'src/app/shared/models/main/note'
import { Pair } from 'src/app/shared/models/main/pair'
import { Quiz } from 'src/app/shared/models/main/quiz'
import { DdcUserSubscription } from 'src/app/shared/models/main/subscription'
import { Word } from 'src/app/shared/models/main/word'
import { DdcNotAvailableComponent } from '../../shared/ddc/ddc-not-available/ddc-not-available.component'
import { DeleteWordPairDicCommand } from '../commands/delete-word-pair-dic/delete-word-pair-dic.command'
import { DdcUserProfilePicDialogComponent } from '../ddc-user-profile-pic-dialog/ddc-user-profile-pic-dialog.component'

// --- Tab UI state management ---

export interface ProfileTabUIState {
  isSelected: boolean
  isLoading: boolean
  scrollLoading: boolean
  end: boolean
  isEmpty: boolean
  fetchError: string
  isLoaded: boolean
  isPgnLoading: boolean
  currentPage: number
  pageInfoLabel: string
}

const PROFILE_TAB_KEYS = ['words', 'pairs', 'dictionaries', 'notes', 'quizzes', 'figures'] as const
type ProfileTabKey = (typeof PROFILE_TAB_KEYS)[number]

function createDefaultProfileTabUI(): Record<ProfileTabKey, ProfileTabUIState> {
  const map = {} as Record<ProfileTabKey, ProfileTabUIState>
  for (const key of PROFILE_TAB_KEYS) {
    map[key] = {
      isSelected: false,
      isLoading: false,
      scrollLoading: false,
      end: false,
      isEmpty: false,
      fetchError: 'Empty.',
      isLoaded: false,
      isPgnLoading: false,
      currentPage: 1,
      pageInfoLabel: 'Scroll down to load more',
    }
  }
  return map
}

@Component({
  selector: 'ddc-user-profile',
  templateUrl: './ddc-user-profile.component.html',
  styleUrls: ['./ddc-user-profile.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    DdcTabsChipsComponent,
    RouterModule,
    DdcNotAvailableComponent,
    DdcLoadingSkeletonComponent,
    InfiniteScrollDirective,
    DdcContentCardComponent,
    DdcUserProfileCardComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcUserProfileComponent {
  // Consolidated tab UI state — replaces per-type booleans/strings/numbers
  _tabUI = createDefaultProfileTabUI()

  // Data arrays
  _words: Word[] = []
  _pairs: Pair[] = []
  _dictionaries: Dictionary[] = []
  _notes: Note[] = []
  _quizzes: Quiz[] = []
  _figures: Figure[] = []

  // Total counts
  _totalCounts: Record<string, number> = {}

  // Profile info
  _isUserInfoLoading: boolean = true
  _userProfilePic: string
  private _DefaultProfilePic: string =
    'https://st3.depositphotos.com/6672868/13701/v/600/depositphotos_137014128-stock-illustration-user-profile-icon.jpg'
  _username: string
  _loggedInUsername: string
  _publicUserFound: boolean = true
  _email: string
  _country: string
  _city: string
  _bio: string
  _favLanguage1: string
  _favLanguage2: string
  _favLanguage3: string
  _userSubscription: DdcUserSubscription = 'Free Tier'
  _followers: number
  _following: number
  _words_count: number
  _pairs_count: number
  _dics_count: number
  _isOwnProfile: boolean = false
  _isFollowed: boolean = false
  protected _isVerified: boolean = false

  _isMobile: boolean = false
  _iconNames = DaydicTypesIconNames
  _theme$: Observable<string>

  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false

  scroll_options: AnimationOptions = {
    path: '/assets/lottie/scroll_down.json',
    loop: true,
    autoplay: true,
  }

  loading_options: AnimationOptions = {
    path: '/assets/lottie/loading2.json',
    loop: true,
    autoplay: true,
  }

  // Content type config for dialogs and delete
  private readonly CONTENT_CONFIG: Record<
    ProfileTabKey,
    { label: string; dialogKey: string; contentTypeId: number }
  > = {
    words: { label: 'Word', dialogKey: 'word', contentTypeId: ContentTypes.WORD },
    pairs: { label: 'Pair', dialogKey: 'pair', contentTypeId: ContentTypes.PAIR },
    dictionaries: { label: 'Dictionary', dialogKey: 'dictionary', contentTypeId: ContentTypes.DIC },
    notes: { label: 'Note', dialogKey: 'note', contentTypeId: ContentTypes.NOTE },
    quizzes: { label: 'Quiz', dialogKey: 'quiz', contentTypeId: ContentTypes.QUIZ },
    figures: { label: 'Figure', dialogKey: 'figure', contentTypeId: ContentTypes.FIGURE },
  }

  // Service mappings for public profile fetches (non-own profile)
  private publicFetchServices: Record<
    ProfileTabKey,
    (username: string, page: number, pageSize: number) => Observable<any>
  >

  private themeSubscription: Subscription
  private routeSubscription: Subscription
  private userInfoSubscription: Subscription

  constructor(
    private cdr: ChangeDetectorRef,
    private logger: DdcLoggerService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private word_service: WordService,
    private pair_service: PairService,
    private dic_service: DictionaryService,
    private note_service: NoteService,
    private quiz_service: QuizService,
    private figure_service: FigureService,
    private user_srv: UserService,
    private network_srv: NetworkService,
    private libraryState: LibraryStateService,
    public themeService: ThemeService,
    private store: Store<AppState>,
    private deleteWordPairDicCommand: DeleteWordPairDicCommand,
  ) {
    this._theme$ = this.store.select(getTheme)

    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    this.publicFetchServices = {
      words: (u: string, p: number, ps: number): Observable<any> =>
        this.word_service.get_user_words_page(u, p, ps),
      pairs: (u: string, p: number, ps: number): Observable<any> =>
        this.pair_service.get_user_pairs_page(u, p, ps),
      dictionaries: (u: string, p: number, ps: number): Observable<any> =>
        this.dic_service.get_user_dics_page(u, p, ps),
      notes: (u: string, p: number, ps: number): Observable<any> =>
        this.note_service.get_user_notes_page(u, p, ps),
      quizzes: (u: string, p: number, ps: number): Observable<any> =>
        this.quiz_service.get_quizzes_page(u, p, ps),
      figures: (u: string, p: number, ps: number): Observable<any> =>
        this.figure_service.get_figures_page(u, p, ps),
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this._isMobile = event.target.innerWidth < 1224
  }

  checkIfMobile(): void {
    this._isMobile = window.innerWidth < 500
  }

  ngOnInit(): void {
    this.checkIfMobile()

    this.routeSubscription = this.route.params.subscribe(params => {
      const username = params['username']
      if (username) {
        this._username = username
        if (this._loggedInUsername) {
          this.loadProfileData()
        }
      }
    })

    this.userInfoSubscription = this.user_srv.userInfo$.subscribe((res: any) => {
      if (res && res.username) {
        this._loggedInUsername = res.username
        this.loadProfileData()
      }
    })
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe()
    }
    if (this.userInfoSubscription) {
      this.userInfoSubscription.unsubscribe()
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe()
    }
  }

  // --- Profile loading ---

  private loadProfileData(): void {
    this._isOwnProfile = this._loggedInUsername === this._username
    const profileObs = this._isOwnProfile
      ? this.user_srv.get_user_profile()
      : this.user_srv.get_public_user_info(this._username)

    profileObs.subscribe((res: any) => {
      this.mapProfileResponse(res)
      if (this._isOwnProfile) {
        this.bindOwnSubscriptions()
      }
      this.clickedChip('dictionaries')
    })
  }

  private mapProfileResponse(res: any): void {
    this._userProfilePic = res.profile_pic
      ? Media.ASSETS + res.profile_pic
      : this._DefaultProfilePic
    this._isUserInfoLoading = false
    this._email = res.email
    this._username = res.username
    if (!this._isOwnProfile && !this._username) {
      this.router.navigate(['/'])
      return
    }
    this._country = res.country
    this._city = res.city
    this._bio = res.bio
    this._favLanguage1 = res.favLanguage1
    this._favLanguage2 = res.favLanguage2
    this._favLanguage3 = res.favLanguage3
    this._followers = res.followers.length
    this._following = res.following.length
    this._words_count = res.words_count
    this._pairs_count = res.pairs_count
    this._dics_count = res.dics_count
    this._userSubscription = res.subscription as DdcUserSubscription
    this._isVerified = res.is_verified
    if (!this._isOwnProfile) {
      this._isFollowed = res.is_followed
    }
  }

  private bindOwnSubscriptions(): void {
    for (const key of PROFILE_TAB_KEYS) {
      this.libraryState.getItems$(key).subscribe(items => {
        this.setDataArray(key, items)
        this.cdr.detectChanges()
      })

      this.libraryState.getLoading$(key).subscribe(loading => {
        this._tabUI[key].scrollLoading = loading
        this.cdr.detectChanges()
      })
    }

    this.libraryState.totalItems$.subscribe(totals => {
      this._totalCounts = { ...totals }
      this.cdr.detectChanges()
    })
  }

  // --- Tab navigation ---

  clickedChip(name: string): void {
    this.logger.info('DdcUserProfileComponent | clickedChip() | name:', name)

    for (const key of PROFILE_TAB_KEYS) {
      this._tabUI[key].isSelected = false
    }

    const tab = this._tabUI[name as ProfileTabKey]
    if (!tab) return
    tab.isSelected = true

    if (!tab.isLoaded) {
      tab.currentPage = 1
      this.loadTabData(name as ProfileTabKey)
    }
  }

  // --- Generic data loading ---

  loadTabData(key: ProfileTabKey): void {
    this.logger.info(`DdcUserProfileComponent | loadTabData(${key})`)
    const tab = this._tabUI[key]
    tab.isLoading = true
    tab.scrollLoading = true
    tab.pageInfoLabel = 'Loading...'
    tab.isEmpty = false
    tab.fetchError = ''
    tab.isPgnLoading = !tab.isPgnLoading

    if (this._isOwnProfile) {
      this.libraryState
        .loadItems(key as ContentTypeKey, true)
        .then(() => {
          tab.end = false
          tab.pageInfoLabel = 'Scroll down to load more'
          tab.isLoading = false
          tab.scrollLoading = false
          tab.isLoaded = true
          tab.isPgnLoading = !tab.isPgnLoading
        })
        .catch(error => {
          this.logger.error(`Error loading ${key}:`, error)
          tab.end = true
          tab.isEmpty = true
          tab.fetchError = error?.message || `Failed to load ${key}`
          tab.pageInfoLabel = 'Error loading items'
          tab.isLoading = false
          tab.scrollLoading = false
          tab.isPgnLoading = !tab.isPgnLoading
        })
    } else {
      this.publicFetchServices[key](
        this._username,
        tab.currentPage,
        DefaultPagination.OBJECT_PER_PAGE,
      ).subscribe(
        (res: any) => {
          this.setDataArray(key, res.results || [])
          this._totalCounts[key] = res.count || 0
          tab.end = false
          tab.pageInfoLabel = 'Scroll down to load more'
          tab.isLoading = false
          tab.scrollLoading = false
          tab.isLoaded = true
          tab.isPgnLoading = !tab.isPgnLoading
        },
        (error: any) => {
          this.logger.error(`Error loading ${key}:`, error)
          tab.end = true
          tab.isEmpty = true
          tab.fetchError = error?.message || `Failed to load ${key}`
          tab.pageInfoLabel = 'Error loading items'
          tab.isLoading = false
          tab.scrollLoading = false
          tab.isPgnLoading = !tab.isPgnLoading
        },
      )
    }
  }

  // --- Generic scroll / append ---

  onTabScroll(key: ProfileTabKey): void {
    const tab = this._tabUI[key]
    if (tab.scrollLoading || tab.end) return
    tab.currentPage++
    this.appendTabData(key)
  }

  private appendTabData(key: ProfileTabKey): void {
    this.logger.info(`DdcUserProfileComponent | appendTabData(${key})`)
    const tab = this._tabUI[key]
    tab.scrollLoading = true
    tab.pageInfoLabel = 'Loading...'
    tab.isPgnLoading = !tab.isPgnLoading

    this.publicFetchServices[key](
      this._username,
      tab.currentPage,
      DefaultPagination.OBJECT_PER_PAGE,
    ).subscribe((res: any) => {
      if (res) {
        if (res.detail !== 'Invalid page.') {
          const current = this.getDataArray(key)
          this.setDataArray(key, current.concat(res.results))
          tab.end = false
          tab.pageInfoLabel = 'Scroll down to load more'
        } else {
          tab.end = true
          tab.pageInfoLabel = `All ${key} loaded.`
        }
        tab.isPgnLoading = !tab.isPgnLoading
        tab.scrollLoading = false
      }
    })
  }

  // --- Data array accessors ---

  getDataArray(key: ProfileTabKey): any[] {
    switch (key) {
      case 'words':
        return this._words
      case 'pairs':
        return this._pairs
      case 'dictionaries':
        return this._dictionaries
      case 'notes':
        return this._notes
      case 'quizzes':
        return this._quizzes
      case 'figures':
        return this._figures
    }
  }

  private setDataArray(key: ProfileTabKey, items: any[]): void {
    switch (key) {
      case 'words':
        this._words = items
        break
      case 'pairs':
        this._pairs = items
        break
      case 'dictionaries':
        this._dictionaries = items as Dictionary[]
        break
      case 'notes':
        this._notes = items as Note[]
        break
      case 'quizzes':
        this._quizzes = items as Quiz[]
        break
      case 'figures':
        this._figures = items as Figure[]
        break
    }
  }

  // --- Generic dialog methods ---

  private getMobileDialogConfig(type: string, element: any): MatDialogConfig {
    let themeClass = ''
    this._theme$
      .subscribe(theme => {
        themeClass = theme
      })
      .unsubscribe()
    const config = new MatDialogConfig()
    config.panelClass = ['mobile-sheet-container', themeClass]
    config.position = { bottom: '0' }
    config.width = '100%'
    config.height = '80vh'
    config.maxWidth = '100vw'
    config.hasBackdrop = true
    config.backdropClass = 'mobile-sheet-backdrop'
    config.enterAnimationDuration = '300ms'
    config.exitAnimationDuration = '200ms'
    config.data = { type, element }
    return config
  }

  openReadDialog(key: ProfileTabKey, index: number): void {
    this.logger.info(`DdcUserProfileComponent | openReadDialog(${key}) | index:`, index)
    const config = this.CONTENT_CONFIG[key]
    const element = this.getDataArray(key)[index]
    const type = `read-${config.dialogKey}`
    if (this._isMobile) {
      this.dialog.open(DdcMobileSheetComponent, this.getMobileDialogConfig(type, element))
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type, element }
      this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  openEditDialog(key: ProfileTabKey, index: number): void {
    this.logger.info(`DdcUserProfileComponent | openEditDialog(${key}) | index:`, index)
    const config = this.CONTENT_CONFIG[key]
    const element = this.getDataArray(key)[index]
    const type = `edit-${config.dialogKey}`
    if (this._isMobile) {
      this.dialog.open(DdcMobileSheetComponent, this.getMobileDialogConfig(type, element))
    } else {
      const dialogConfig = new MatDialogConfig()
      dialogConfig.width = '600px'
      dialogConfig.maxWidth = '90vw'
      dialogConfig.hasBackdrop = true
      dialogConfig.data = { type, element }
      this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
    }
  }

  openDeleteDialog(key: ProfileTabKey, index: number): void {
    this.logger.info(`DdcUserProfileComponent | openDeleteDialog(${key}) | index:`, index)
    const config = this.CONTENT_CONFIG[key]
    const items = this.getDataArray(key)
    const element = items[index]
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: { type: config.label, element },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        this.deleteWordPairDicCommand.setArgs({
          content_type_id: config.contentTypeId,
          object_id: element.id,
        })
        this.deleteWordPairDicCommand.execute().subscribe(success => {
          if (success) {
            const idx = items.findIndex(item => item.id === element.id)
            if (idx !== -1) {
              items.splice(idx, 1)
              this.cdr.detectChanges()
            }
          }
        })
      }
    })
  }

  // --- Profile / follow actions ---

  onFollowToggle(isFollowing: boolean): void {
    this._isFollowed = isFollowing
    const followCommand = this._isFollowed ? 'follow' : 'unfollow'
    this.network_srv.command(this._username, followCommand).subscribe(() => {})
  }

  openProfilePictureDialog(): void {
    const dialogRef = this.dialog.open(DdcUserProfilePicDialogComponent)
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'dialogClosed') {
        window.location.reload()
      }
    })
  }

  followersClicked(): void {
    this.router.navigate(['/network', LibraryChips.FOLLOWERS])
  }

  followingClicked(): void {
    this.router.navigate(['/network', LibraryChips.FOLLOWING])
  }
}
